import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { StarIcon } from 'lucide-react';
import { LOOX_PRODUCT_ID, LOOX_PUBLIC_STORE_ID } from '../lib/config';
import { SectionHeading } from './SectionHeading';
type Review = {
  id: string;
  name: string;
  rating: number;
  body: string;
};
type LooxReviewResponse = {
  reviews?: Array<{
    id: string;
    rating: number | string;
    body: string;
    reviewer?: {
      name?: string;
    };
  }>;
};
const FALLBACK_REVIEWS: Review[] = [{
  id: 'manual-martu',
  name: 'Martu F.',
  rating: 4,
  body: 'Me gustó mucho, lo leí en un día y me cayó la ficha de que tengo que frenar un poco, por mí misma.'
}, {
  id: 'manual-tati',
  name: 'Tati D.',
  rating: 5,
  body: '¡Excelente!'
}];
const REVIEW_API_URL = `https://storefront-api.loox.io/storefront/v1/store/${LOOX_PUBLIC_STORE_ID}/product-reviews?product_id=${LOOX_PRODUCT_ID}&limit=15`;
const REFRESH_INTERVAL = 5 * 60 * 1000;
function RatingStars({
  rating
}: {
  rating: number;
}) {
  return <div className="flex gap-1 text-sage" aria-label={`${rating} de 5 estrellas`} data-id="element-124" data-mp="h558jwk91lug">
      {Array.from({
      length: 5
    }, (_, index) => <StarIcon key={index} size={16} fill={index < rating ? 'currentColor' : 'none'} strokeWidth={1.8} aria-hidden="true" data-id="element-125" data-mp="m2ydyndqabto" />)}
    </div>;
}
function toReview(review: NonNullable<LooxReviewResponse['reviews']>[number]): Review | null {
  const body = review.body?.trim();
  const name = review.reviewer?.name?.trim();
  const rating = Number(review.rating);
  if (!review.id || !body || !name || !Number.isFinite(rating)) return null;
  return {
    id: review.id,
    name,
    rating: Math.max(1, Math.min(5, rating)),
    body
  };
}
export function Testimonials() {
  const [reviews, setReviews] = useState<Review[]>(FALLBACK_REVIEWS);
  const [isLoading, setIsLoading] = useState(true);
  const [isLive, setIsLive] = useState(false);
  useEffect(() => {
    let isMounted = true;
    const loadReviews = async () => {
      try {
        const response = await fetch(REVIEW_API_URL);
        if (!response.ok) throw new Error('No se pudieron cargar las reseñas de Loox.');
        const payload = (await response.json()) as LooxReviewResponse;
        const liveReviews = (payload.reviews ?? []).map(toReview).filter((review): review is Review => review !== null).slice(0, 15);
        if (isMounted && liveReviews.length > 0) {
          setReviews(liveReviews);
          setIsLive(true);
        }
      } catch {
        if (isMounted) setIsLive(false);
      } finally {
        if (isMounted) setIsLoading(false);
      }
    };
    void loadReviews();
    const refreshId = window.setInterval(() => void loadReviews(), REFRESH_INTERVAL);
    return () => {
      isMounted = false;
      window.clearInterval(refreshId);
    };
  }, []);
  return <section className="w-full bg-cream py-20 md:py-28" data-id="element-126" data-mp="qtajdq8iw94s">
      <div className="mx-auto max-w-5xl px-6" data-id="element-127" data-mp="qvws3t75guol">
        <SectionHeading eyebrow="Testimonios verificados" title="Lo que dicen quienes lo leyeron" data-id="element-128" data-mp="32fukhmf8yhj3" />

        <div className="mt-6 flex items-center justify-center gap-2 text-center" data-id="element-129" data-mp="37e5ubx5jks1b">
          <RatingStars rating={5} data-id="element-130" data-mp="3cgelcnyutk0s" />
          <p className="font-sans text-xs uppercase tracking-[0.16em] text-muted" data-id="element-131" data-mp="3cd91bn5318bx">
            Opiniones de lectores
          </p>
        </div>

        {isLoading && <p className="mt-8 text-center font-sans text-sm text-ink-soft" role="status" data-id="element-132" data-mp="2jrz8o8373dsm">
            Actualizando reseñas verificadas…
          </p>}

        <div className="mt-10 grid grid-cols-1 gap-5 md:grid-cols-2 lg:grid-cols-3" data-id="element-133" data-mp="2sqktk9w534wk">
          {reviews.map((review, index) => <motion.blockquote key={review.id} initial={{
          opacity: 0,
          y: 16
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true,
          margin: '-40px'
        }} transition={{
          duration: 0.45,
          ease: 'easeOut',
          delay: index % 3 * 0.07
        }} className="flex min-h-56 flex-col justify-between bg-cream-deep p-7" data-id="element-134" data-mp="2xjdusnsefv3w" data-mpi={index}>
              <div data-id="element-135" data-mp="1gzdb70tyknt7">
                <RatingStars rating={review.rating} data-id="element-136" data-mp="1gvpig3wc85fj" />
                <p className="mt-5 font-serif text-xl italic leading-snug text-ink" data-id="element-137" data-mp="1vnro64l39snh">
                  “{review.body}”
                </p>
              </div>
              <footer className="mt-8 font-sans text-xs uppercase tracking-[0.2em] text-muted" data-id="element-138" data-mp="1qu5bicexdoro">
                {review.name}
              </footer>
            </motion.blockquote>)}
        </div>

        <p className="mt-8 text-center font-sans text-xs leading-relaxed text-muted" data-id="element-139" data-mp="2elkd22yvo21v">
          {isLive ? 'Reseñas publicadas en Respira Tranquilidad. Se actualizan automáticamente desde Loox.' : 'Mostrando reseñas publicadas recientemente.'}
        </p>
      </div>
    </section>;
}